<template>
    <div class="stake-optimizer" v-if="walletConnected">
        <!-- Loading -->
        <div v-if="loading" class="compass-card mb-4">
            <div class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-primary"></div>
                <p class="small text-muted mt-2">Analyzing your stake positions...</p>
            </div>
        </div>

        <!-- Optimization opportunities found -->
        <div v-else-if="opportunities.length > 0" class="compass-card compass-card--accent mb-4">
            <div class="d-flex align-items-center gap-2 mb-3">
                <i class="fas fa-bolt text-warning"></i>
                <h4 class="mb-0 text-gradient">Optimize Your Staking</h4>
            </div>
            <p class="text-muted small mb-3">
                We found {{ opportunities.length }} stake account{{ opportunities.length > 1 ? 's' : '' }}
                earning below Compass rates. Move to Compass for better yield.
            </p>

            <div v-for="opp in opportunities" :key="opp.address" class="border rounded p-3 mb-3">
                <div class="d-flex justify-content-between align-items-start mb-2">
                    <div>
                        <div class="fw-bold small">
                            {{ opp.validatorName || shortenAddress(opp.validator) }}
                        </div>
                        <div class="font-mono-num">{{ formatSol(opp.solBalance) }} SOL</div>
                    </div>
                    <div class="text-end">
                        <div class="small text-muted">Current APY</div>
                        <span class="font-mono-num pct-change pct-negative">{{ opp.currentApy }}%</span>
                        <i class="fas fa-arrow-right mx-1 small text-muted"></i>
                        <span class="font-mono-num pct-change pct-positive">{{ compassApy }}%</span>
                    </div>
                </div>

                <div class="d-flex gap-2 flex-wrap">
                    <button
                        class="compass-btn compass-btn--primary compass-btn--sm flex-fill"
                        @click="instantUnstakeAndRestake(opp)"
                        :disabled="opp.migrating"
                    >
                        <span v-if="opp.migrating" class="spinner-border spinner-border-sm me-1"></span>
                        {{ opp.migrating ? 'Moving...' : 'Instant Move to Compass' }}
                    </button>
                    <button
                        class="compass-btn compass-btn--outline compass-btn--sm"
                        @click="instantUnstakeToSol(opp)"
                        :disabled="opp.migrating"
                        title="Instantly convert stake account to SOL via Marinade"
                    >
                        Unstake → SOL
                    </button>
                    <button
                        v-if="compasssolApy && compasssolMint"
                        class="compass-btn compass-btn--outline compass-btn--sm"
                        @click="moveToCompassSol(opp)"
                        :disabled="opp.migrating"
                        title="Instant unstake → swap to compassSOL for liquid staking"
                    >
                        → compassSOL
                    </button>
                </div>

                <div v-if="opp.error" class="alert alert-danger mt-2 mb-0 small py-1 px-2">{{ opp.error }}</div>
                <div v-if="opp.success" class="alert alert-success mt-2 mb-0 small py-1 px-2">
                    {{ opp.success }}
                    <a v-if="opp.explorerUrl" :href="opp.explorerUrl" target="_blank">View tx &rarr;</a>
                </div>
            </div>

            <!-- Potential savings summary -->
            <div class="compass-info-box compass-info-box--success small" v-if="potentialExtraYield > 0">
                <strong>Potential extra yield:</strong>
                <span class="font-mono-num">+{{ potentialExtraYield.toFixed(4) }} SOL/year</span>
                by moving all underperforming stakes to Compass.
            </div>
        </div>

        <!-- LST optimization -->
        <div v-if="lstOpportunities.length > 0" class="compass-card mb-4">
            <div class="d-flex align-items-center gap-2 mb-3">
                <i class="fas fa-exchange-alt text-info"></i>
                <h4 class="mb-0">LST Optimization</h4>
            </div>
            <p class="text-muted small mb-3">
                You hold LSTs with lower APY than compassSOL. Swap for better yield.
            </p>

            <div v-for="opp in lstOpportunities" :key="opp.mint" class="border rounded p-3 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <div>
                        <span class="fw-bold small">{{ opp.symbol }}</span>
                        <span class="font-mono-num ms-2">{{ formatSol(opp.amount) }}</span>
                    </div>
                    <div class="text-end small">
                        <span class="font-mono-num pct-change pct-negative">{{ opp.currentApy }}%</span>
                        <i class="fas fa-arrow-right mx-1 text-muted"></i>
                        <span class="font-mono-num pct-change pct-positive">{{ compasssolApy }}%</span>
                    </div>
                </div>
                <button
                    class="compass-btn compass-btn--outline compass-btn--sm w-100"
                    @click="swapToCompassSol(opp)"
                    :disabled="opp.migrating"
                >
                    {{ opp.migrating ? 'Swapping...' : `Swap ${opp.symbol} → compassSOL` }}
                </button>
                <div v-if="opp.error" class="alert alert-danger mt-2 mb-0 small py-1 px-2">{{ opp.error }}</div>
                <div v-if="opp.success" class="alert alert-success mt-2 mb-0 small py-1 px-2">{{ opp.success }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import { useSolSdk } from '../composables/useSolSdk';

const MARINADE_REFERRAL = 'D9d7WxBeCeNU8zECA4YKG6ciBKHQG5mqmyygCetvXz7H';
const COMPASS_IDENTITY = 'CMPSSdrTnRQBiBGTyFpdCc3VMNuLWYWaSkE8Zh5z6gbd';
const MSOL_MINT = 'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So';
const SOL_MINT = 'So11111111111111111111111111111111111111112';
const UNSTAKE_FEE_BPS = window.unstakeFeeBPS ?? 30;

export default {
    name: 'StakeOptimizer',
    props: {
        compassVote: { type: String, required: true },
        compassApy: { type: Number, default: 7.2 },
        compasssolApy: { type: Number, default: null },
        compasssolMint: { type: String, default: '' },
        lstPools: { type: Array, default: () => [] },
    },
    data() {
        return {
            loading: false,
            analyzed: false,
            opportunities: [],
            lstOpportunities: [],
            validatorCache: {},
        };
    },
    computed: {
        walletConnected() {
            return !!window.wallet?.publicKey?.value;
        },
        potentialExtraYield() {
            return this.opportunities.reduce((sum, opp) => {
                const currentYield = opp.solBalance * (opp.currentApy / 100);
                const compassYield = opp.solBalance * (this.compassApy / 100);
                return sum + (compassYield - currentYield);
            }, 0);
        },
    },
    mounted() {
        if (this.walletConnected) this.analyze();
        this._walletInterval = setInterval(() => {
            if (this.walletConnected && !this.analyzed && !this.loading) {
                this.analyze();
            }
        }, 3000);
    },
    beforeUnmount() {
        if (this._walletInterval) clearInterval(this._walletInterval);
    },
    methods: {
        formatSol(val) {
            if (!val) return '0';
            return val.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
        },
        shortenAddress(addr) {
            if (!addr) return '—';
            return addr.slice(0, 4) + '...' + addr.slice(-4);
        },

        async analyze() {
            if (!this.walletConnected) return;
            this.loading = true;
            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();
                const walletAddress = window.wallet.publicKey.value.toBase58();

                // Get stake accounts
                const accounts = await sdk.stake.getStakeAccounts(walletAddress);

                // Fetch validator APYs from our backend
                await this.loadValidatorData();

                // Find accounts delegated to non-Compass validators with lower APY
                const opps = [];
                for (const account of accounts) {
                    if (!account.validator || account.validator === this.compassVote) continue;
                    if (account.status !== 'active' && account.status !== 'delegated') continue;

                    const validatorData = this.validatorCache[account.validator];
                    const currentApy = validatorData?.apy
                        ? (validatorData.apy * 100).toFixed(2)
                        : '0.00';

                    // Only flag if APY is significantly lower than Compass
                    if (parseFloat(currentApy) < this.compassApy - 0.5) {
                        opps.push({
                            ...account,
                            currentApy,
                            validatorName: validatorData?.name || null,
                            migrating: false,
                            error: null,
                            success: null,
                            explorerUrl: null,
                        });
                    }
                }

                this.opportunities = opps;

                // Check LST balances for optimization
                await this.analyzeLsts(walletAddress);
                this.analyzed = true;
            } catch (e) {
                console.error('[StakeOptimizer] Analysis failed:', e);
            } finally {
                this.loading = false;
            }
        },

        async loadValidatorData() {
            try {
                const response = await fetch('/validators/ajax');
                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        for (const v of data) {
                            this.validatorCache[v.vote_account || v.voteAccountAddress] = {
                                name: v.name,
                                apy: v.apy || v.score?.apy || 0,
                                commission: v.commission || 0,
                            };
                        }
                    }
                }
            } catch (e) {
                // Validator data is optional — gracefully degrade
                console.warn('[StakeOptimizer] Could not load validator data:', e);
            }
        },

        async analyzeLsts(walletAddress) {
            if (!this.compasssolApy) return;
            try {
                const { Connection, PublicKey } = await import('@solana/web3.js');
                const { TOKEN_PROGRAM_ID } = await import('@solana/spl-token');
                const connection = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');

                const accounts = await connection.getParsedTokenAccountsByOwner(
                    new PublicKey(walletAddress),
                    { programId: TOKEN_PROGRAM_ID }
                );

                // Build LST lookup from SSR data (real APYs) with hardcoded fallbacks
                const knownLsts = {};
                const fallbackLsts = {
                    'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So': { symbol: 'mSOL', apy: 7.0 },
                    'J1toso1uCk3RLmjorhTtrVwY9HJ7X8V9yYac6Y7kGCPn': { symbol: 'jitoSOL', apy: 7.5 },
                    'bSo13r4TkiE4KumL71LsHTPpL2euBYLFx6h9HP3piy1': { symbol: 'bSOL', apy: 7.0 },
                };
                // Use real APY data from lstPools prop if available
                for (const pool of this.lstPools) {
                    if (pool.poolMint && pool.poolMint !== this.compasssolMint && pool.apy) {
                        knownLsts[pool.poolMint] = { symbol: pool.symbol || pool.name, apy: pool.apy };
                    }
                }
                // Fill in any missing LSTs from fallbacks
                for (const [mint, data] of Object.entries(fallbackLsts)) {
                    if (!knownLsts[mint]) knownLsts[mint] = data;
                }

                const opps = [];
                for (const { account } of accounts.value) {
                    const info = account.data.parsed?.info;
                    if (!info) continue;
                    const mint = info.mint;
                    if (mint === this.compasssolMint) continue; // Already compassSOL

                    const lstInfo = knownLsts[mint];
                    if (!lstInfo) continue;

                    const amount = parseFloat(info.tokenAmount?.uiAmountString || '0');
                    if (amount <= 0.001) continue;

                    if (lstInfo.apy < this.compasssolApy) {
                        opps.push({
                            mint,
                            symbol: lstInfo.symbol,
                            currentApy: lstInfo.apy.toFixed(2),
                            amount,
                            migrating: false,
                            error: null,
                            success: null,
                        });
                    }
                }

                this.lstOpportunities = opps;
            } catch (e) {
                console.warn('[StakeOptimizer] LST analysis failed:', e);
            }
        },

        /**
         * Send Compass unstake fee as a SOL transfer (best-effort).
         */
        async sendCompassFee(conn, solAmount) {
            if (UNSTAKE_FEE_BPS <= 0 || solAmount <= 0) return null;
            const feeSol = solAmount * (UNSTAKE_FEE_BPS / 10000);
            if (feeSol < 0.000001) return null;
            try {
                const { PublicKey, Transaction, SystemProgram } = await import('@solana/web3.js');
                const tx = new Transaction().add(
                    SystemProgram.transfer({
                        fromPubkey: window.wallet.publicKey.value,
                        toPubkey: new PublicKey(COMPASS_IDENTITY),
                        lamports: Math.floor(feeSol * 1e9),
                    })
                );
                const hash = await conn.getLatestBlockhash();
                tx.recentBlockhash = hash.blockhash;
                tx.feePayer = window.wallet.publicKey.value;
                return await window.wallet.sendTransaction(tx, conn);
            } catch (e) {
                console.warn('[StakeOptimizer] Compass fee transfer failed:', e);
                return null;
            }
        },

        /**
         * Deposit a stake account into Marinade, returning mSOL.
         * Shared step 1 for both Marinade and Jupiter unstake paths.
         */
        async _depositStakeToMarinade(stakeAddress) {
            const { Marinade, MarinadeConfig } = await import('@marinade.finance/marinade-ts-sdk');
            const { Connection, PublicKey } = await import('@solana/web3.js');
            const conn = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');

            const config = new MarinadeConfig({
                connection: conn,
                publicKey: window.wallet.publicKey.value,
                referralCode: new PublicKey(MARINADE_REFERRAL),
            });
            const marinade = new Marinade(config);

            const stakeAddr = new PublicKey(stakeAddress);
            const depositResult = await marinade.depositStakeAccount(stakeAddr);
            const depositSig = await window.wallet.sendTransaction(depositResult.transaction, conn);
            await conn.confirmTransaction(depositSig, 'confirmed');

            return { conn, marinade };
        },

        /**
         * Get the current mSOL balance after a deposit.
         */
        async _getMsolBalance(conn) {
            const { BN } = await import('@marinade.finance/marinade-ts-sdk');
            const { PublicKey } = await import('@solana/web3.js');
            const mSolMint = new PublicKey(MSOL_MINT);
            const walletKey = window.wallet.publicKey.value;
            const mSolAccounts = await conn.getParsedTokenAccountsByOwner(walletKey, { mint: mSolMint });
            let mSolBalance = new BN(0);
            for (const { account } of mSolAccounts.value) {
                const amt = account.data.parsed?.info?.tokenAmount?.amount;
                if (amt) mSolBalance = mSolBalance.add(new BN(amt));
            }
            return mSolBalance;
        },

        /**
         * Marinade path: deposit stake → mSOL → Marinade liquidUnstake → SOL
         */
        async _marinadeUnstake(stakeAddress, solAmount) {
            const { conn, marinade } = await this._depositStakeToMarinade(stakeAddress);
            const mSolBalance = await this._getMsolBalance(conn);

            const unstakeResult = await marinade.liquidUnstake(mSolBalance);
            const unstakeSig = await window.wallet.sendTransaction(unstakeResult.transaction, conn);
            await conn.confirmTransaction(unstakeSig, 'confirmed');

            // Send Compass fee (best-effort)
            if (solAmount) await this.sendCompassFee(conn, solAmount);

            return { unstakeSig };
        },

        /**
         * Jupiter/Sanctum path: deposit stake → mSOL → Jupiter swap (Sanctum Router) → SOL
         */
        async _jupiterUnstake(stakeAddress) {
            const { conn } = await this._depositStakeToMarinade(stakeAddress);
            const mSolBalance = await this._getMsolBalance(conn);
            const mSolAmount = mSolBalance.toString();

            // Get Jupiter quote for mSOL → SOL
            const quoteResp = await fetch(
                `https://quote-api.jup.ag/v6/quote?inputMint=${MSOL_MINT}&outputMint=${SOL_MINT}&amount=${mSolAmount}&slippageBps=50`
            );
            const quote = await quoteResp.json();
            if (quote.error) throw new Error(quote.error);

            // Build and execute swap transaction
            const walletAddress = window.wallet.publicKey.value.toBase58();
            const swapResp = await fetch('https://quote-api.jup.ag/v6/swap', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    quoteResponse: quote,
                    userPublicKey: walletAddress,
                    wrapAndUnwrapSol: true,
                }),
            });
            const { swapTransaction } = await swapResp.json();
            if (!swapTransaction) throw new Error('Failed to build Jupiter swap transaction');

            const { VersionedTransaction } = await import('@solana/web3.js');
            const tx = VersionedTransaction.deserialize(Buffer.from(swapTransaction, 'base64'));
            const signed = await window.wallet.signTransaction(tx);
            const swapSig = await conn.sendRawTransaction(signed.serialize());

            const solOut = parseInt(quote.outAmount) / 1e9;

            // Send Compass fee (best-effort)
            await this.sendCompassFee(conn, solOut);

            return { swapSig, solOut };
        },

        async instantUnstakeToSol(opp) {
            opp.migrating = true;
            opp.error = null;
            opp.success = null;
            try {
                // Try Jupiter/Sanctum first (often better rates), fall back to Marinade
                try {
                    const { swapSig, solOut } = await this._jupiterUnstake(opp.address);
                    opp.success = `Instantly unstaked ~${solOut.toFixed(4)} SOL via Jupiter/Sanctum!`;
                    opp.explorerUrl = `https://solscan.io/tx/${swapSig}`;
                } catch (jupErr) {
                    console.warn('[StakeOptimizer] Jupiter path failed, trying Marinade:', jupErr);
                    const { unstakeSig } = await this._marinadeUnstake(opp.address, opp.solBalance);
                    opp.success = `Instantly unstaked ${opp.solBalance.toFixed(4)} SOL via Marinade!`;
                    opp.explorerUrl = `https://solscan.io/tx/${unstakeSig}`;
                }
            } catch (e) {
                opp.error = e.message || 'Instant unstake failed';
                console.error('[StakeOptimizer] Instant unstake failed:', e);
            } finally {
                opp.migrating = false;
            }
        },

        async instantUnstakeAndRestake(opp) {
            opp.migrating = true;
            opp.error = null;
            opp.success = null;
            try {
                // Step 1: Instant unstake via Jupiter/Sanctum (fallback to Marinade)
                let solReceived = opp.solBalance * 0.997; // conservative estimate
                try {
                    const { solOut } = await this._jupiterUnstake(opp.address);
                    solReceived = solOut;
                } catch (jupErr) {
                    console.warn('[StakeOptimizer] Jupiter path failed, trying Marinade:', jupErr);
                    await this._marinadeUnstake(opp.address, opp.solBalance);
                }

                // Step 2: Re-stake SOL with Compass (leave 0.005 SOL for tx fee)
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();
                const stakeAmount = Math.max(0, solReceived - 0.005);
                const stakeResult = await sdk.stake.createAndDelegateStake(
                    'browser',
                    stakeAmount,
                    this.compassVote
                );
                opp.success = `Moved ~${stakeAmount.toFixed(4)} SOL to Compass instantly!`;
                opp.explorerUrl = stakeResult.explorerUrl;
            } catch (e) {
                opp.error = e.message || 'Instant move failed';
                console.error('[StakeOptimizer] Instant move failed:', e);
            } finally {
                opp.migrating = false;
            }
        },

        /**
         * Convert a stake account to compassSOL:
         * instant unstake → SOL → Jupiter swap → compassSOL
         */
        async moveToCompassSol(opp) {
            opp.migrating = true;
            opp.error = null;
            opp.success = null;

            try {
                // Step 1: Instant unstake via Jupiter/Sanctum (fallback to Marinade)
                let solReceived = opp.solBalance * 0.997;
                try {
                    const { solOut } = await this._jupiterUnstake(opp.address);
                    solReceived = solOut;
                } catch (jupErr) {
                    console.warn('[StakeOptimizer] Jupiter unstake failed, trying Marinade:', jupErr);
                    await this._marinadeUnstake(opp.address, opp.solBalance);
                }

                // Step 2: Swap SOL → compassSOL via Jupiter
                const solLamports = Math.floor(solReceived * 1e9);
                const quoteResp = await fetch(
                    `https://quote-api.jup.ag/v6/quote?inputMint=${SOL_MINT}&outputMint=${this.compasssolMint}&amount=${solLamports}&slippageBps=50`
                );
                const quote = await quoteResp.json();
                if (quote.error) throw new Error(quote.error);

                const swapResp = await fetch('https://quote-api.jup.ag/v6/swap', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        quoteResponse: quote,
                        userPublicKey: window.wallet.publicKey.value.toBase58(),
                        wrapAndUnwrapSol: true,
                    }),
                });
                const { swapTransaction } = await swapResp.json();
                if (!swapTransaction) throw new Error('Failed to build swap transaction');

                const { VersionedTransaction, Connection } = await import('@solana/web3.js');
                const conn = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');
                const tx = VersionedTransaction.deserialize(Buffer.from(swapTransaction, 'base64'));
                const signed = await window.wallet.signTransaction(tx);
                const sig = await conn.sendRawTransaction(signed.serialize());

                const compassSolOut = parseInt(quote.outAmount) / 1e9;
                opp.success = `Converted to ~${compassSolOut.toFixed(4)} compassSOL!`;
                opp.explorerUrl = `https://solscan.io/tx/${sig}`;
            } catch (e) {
                opp.error = e.message || 'Conversion to compassSOL failed';
                console.error('[StakeOptimizer] compassSOL conversion failed:', e);
            } finally {
                opp.migrating = false;
            }
        },

        async swapToCompassSol(opp) {
            opp.migrating = true;
            opp.error = null;
            opp.success = null;

            try {
                // Use Jupiter swap API to swap LST → compassSOL
                const inputMint = opp.mint;
                const outputMint = this.compasssolMint;
                const amountLamports = Math.floor(opp.amount * 1e9);

                const quoteResponse = await fetch(
                    `https://quote-api.jup.ag/v6/quote?inputMint=${inputMint}&outputMint=${outputMint}&amount=${amountLamports}&slippageBps=50`
                );
                const quote = await quoteResponse.json();

                if (quote.error) throw new Error(quote.error);

                const swapResponse = await fetch('https://quote-api.jup.ag/v6/swap', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        quoteResponse: quote,
                        userPublicKey: window.wallet.publicKey.value.toBase58(),
                        wrapAndUnwrapSol: true,
                    }),
                });
                const { swapTransaction } = await swapResponse.json();

                const { VersionedTransaction } = await import('@solana/web3.js');
                const txBuf = Buffer.from(swapTransaction, 'base64');
                const tx = VersionedTransaction.deserialize(txBuf);
                const signed = await window.wallet.signTransaction(tx);

                const { Connection } = await import('@solana/web3.js');
                const connection = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');
                const sig = await connection.sendRawTransaction(signed.serialize());

                opp.success = `Swapped ${opp.symbol} → compassSOL!`;
            } catch (e) {
                opp.error = e.message || 'Swap failed';
                console.error('[StakeOptimizer] Swap failed:', e);
            } finally {
                opp.migrating = false;
            }
        },
    },
};
</script>
